/* eslint-disable */
import { useState, useEffect } from 'react';
import { formatDayMonth, formatMonthShort } from '../../../../utils/formatters';
import DataTable, { StatusBadge, type Column } from '../../../../components/DataTable';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import {
  MATRIX_STATUS_CONFIG, TIER_FREQUENCIES,
  getWeekNumber
} from './constants';
import RoomViewModal from './RoomViewModal';
import RoomEditModal from './RoomEditModal';
import BulkRoomBuilder from './BulkRoomBuilder';
import ImportRoomsFromProposalModal from './ImportRoomsFromProposalModal';

interface RoomContext {
  type: string;
  id: string;
  name: string;
  propertyId: string;
  propertyName: string;
}

interface PropertyRoomsViewProps {
  property: Record<string, any>;
  onBack: () => void;
  onEdit: () => void;
  onManage: (view: string) => void;
  canEdit: boolean;
  supabase: any;
  onRefresh?: () => void;
  setCertificatesContext: (ctx: RoomContext) => void;
  setBookingsContext: (ctx: RoomContext) => void;
  setServicesContext: (ctx: RoomContext) => void;
}

export default function PropertyRoomsView({
  property, canEdit, supabase, onRefresh,
  setCertificatesContext, setBookingsContext, setServicesContext
}: PropertyRoomsViewProps) {
  // Matrix opens as a full-screen overlay — only the list lives inline in
  // the tab body now. State is ephemeral (not persisted) since the open
  // action is a one-off "take a look" rather than a preferred default.
  const [matrixFullscreen, setMatrixFullscreen] = useState(false);

  const [rooms, setRooms] = useState<Record<string, any>[]>([]);
  const [serviceRequests, setServiceRequests] = useState<Record<string, any>[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [viewingRoom, setViewingRoom] = useState<Record<string, any> | null>(null);
  const [editingRoom, setEditingRoom] = useState<Record<string, any> | null>(null);

  // Matrix state
  const [matrixWeekOffset, setMatrixWeekOffset] = useState(0);
  const [matrixMonthOffset, setMatrixMonthOffset] = useState(0);
  const [numWeeks, setNumWeeks] = useState(20);
  const [numMonths, setNumMonths] = useState(12);
  const [matrixGranularity, setMatrixGranularity] = useState(() => {
    return localStorage.getItem('posMatrixGranularity') || 'weekly';
  });

  // Persist matrix granularity (weekly/monthly) across opens.
  useEffect(() => {
    localStorage.setItem('posMatrixGranularity', matrixGranularity);
  }, [matrixGranularity]);

  // Calculate columns to fill screen
  useEffect(() => {
    const calculateColumns = () => {
      const sidebarWidth = 200;
      const pagePadding = 40;
      const cardPadding = 28;
      const fixedColWidth = 100;
      const cellWidth = matrixGranularity === 'weekly' ? 28 : 50;
      const availableWidth = window.innerWidth - sidebarWidth - pagePadding - cardPadding - fixedColWidth;
      if (matrixGranularity === 'weekly') {
        setNumWeeks(Math.max(12, Math.floor(availableWidth / cellWidth)));
      } else {
        setNumMonths(Math.max(6, Math.floor(availableWidth / cellWidth)));
      }
    };
    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, [matrixFullscreen, matrixGranularity]);

  useEffect(() => {
    loadRooms();
  }, [property.id]);

  const loadRooms = async () => {
    if (!supabase || !property.id) {
      setRoomsLoading(false);
      return;
    }
    try {
      const { data: roomsData, error: roomsError } = await supabase
        .from('hosp_rooms')
        .select('*')
        .eq('property_id', property.id)
        .order('room_name');
      if (roomsError) throw roomsError;

      const { data: servicesData } = await supabase
        .from('hosp_service_requests')
        .select('id, room_ids, status, requested_date')
        .eq('property_id', property.id)
        .in('status', ['pending', 'confirmed', 'completed']);

      setServiceRequests(servicesData || []);

      const roomServiceMap: Record<string, any> = {};
      (servicesData || []).filter((s: any) => ['pending', 'confirmed'].includes(s.status)).forEach((svc: any) => {
        (svc.room_ids || []).forEach((roomId: string) => {
          if (!roomServiceMap[roomId]) {
            roomServiceMap[roomId] = svc;
          }
        });
      });

      const enrichedRooms = (roomsData || []).map((room: any, idx: number) => ({
        ...room,
        displayName: room.room_name || room.room_number || (room.bed_count ? `${room.bed_count} Bed Room` : `Room ${idx + 1}`),
        pendingService: roomServiceMap[room.id] || null,
        daysSinceService: room.last_service_date
          ? Math.floor((new Date().getTime() - new Date(room.last_service_date).getTime()) / (1000 * 60 * 60 * 24))
          : null
      }));

      setRooms(enrichedRooms);
    } catch (err: any) {
      console.error('Error loading rooms:', err);
    } finally {
      setRoomsLoading(false);
    }
  };

  // Calculate room status for matrix
  const calculateRoomStatus = (room: Record<string, any>, checkDate: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const cellDate = new Date(checkDate);
    cellDate.setHours(0, 0, 0, 0);

    const tier = property?.hospitality_tier || 'assured';
    const frequency = TIER_FREQUENCIES[tier] || 365;
    const dueSoonThreshold = 30;
    const gracePeriodDays = 30;

    const serviceDate = room.last_service_date ? new Date(room.last_service_date) : null;
    if (serviceDate) serviceDate.setHours(0, 0, 0, 0);

    let dueDate = null;
    if (serviceDate) {
      dueDate = new Date(serviceDate);
      dueDate.setDate(dueDate.getDate() + frequency);
    }

    let dueSoonDate = null;
    let overdueDate = null;
    if (dueDate) {
      dueSoonDate = new Date(dueDate);
      dueSoonDate.setDate(dueSoonDate.getDate() - dueSoonThreshold);
      overdueDate = new Date(dueDate);
      overdueDate.setDate(overdueDate.getDate() + gracePeriodDays);
    }

    const booking = serviceRequests.find(r =>
      r.room_ids?.includes(room.id) && ['pending', 'confirmed'].includes(r.status)
    );

    if (booking) {
      const bookingDate = new Date(booking.requested_date);
      bookingDate.setHours(0, 0, 0, 0);
      const bookingWeekStart = new Date(bookingDate);
      bookingWeekStart.setDate(bookingWeekStart.getDate() - bookingWeekStart.getDay());
      const bookingWeekEnd = new Date(bookingWeekStart);
      bookingWeekEnd.setDate(bookingWeekEnd.getDate() + 6);

      if (cellDate >= bookingWeekStart && cellDate <= bookingWeekEnd) return 'booked';

      if (cellDate > bookingDate) {
        const projectedDueDate = new Date(bookingDate);
        projectedDueDate.setDate(projectedDueDate.getDate() + frequency);
        const projectedDueSoonDate = new Date(projectedDueDate);
        projectedDueSoonDate.setDate(projectedDueSoonDate.getDate() - dueSoonThreshold);
        const projectedOverdueDate = new Date(projectedDueDate);
        projectedOverdueDate.setDate(projectedOverdueDate.getDate() + gracePeriodDays);

        if (cellDate >= bookingDate && cellDate < projectedDueSoonDate) return 'certified';
        if (cellDate >= projectedDueSoonDate && cellDate <= projectedOverdueDate) return 'due-soon';
        if (cellDate > projectedOverdueDate) return 'overdue';
        return 'future';
      }
    }

    if (serviceDate && dueDate && dueSoonDate && overdueDate) {
      if (cellDate >= serviceDate && cellDate < dueSoonDate) return 'certified';
      if (cellDate >= dueSoonDate && cellDate <= overdueDate) return 'due-soon';
      if (cellDate > overdueDate) return 'overdue';
      if (cellDate < serviceDate) return 'pending';
    }

    return 'pending';
  };

  const handleAddRoom = async (roomName: string) => {
    if (!roomName?.trim()) return;
    try {
      // Auto-generate room_number from existing count
      const nextNum = String(rooms.length + 1).padStart(3, '0');
      const { error } = await supabase
        .from('hosp_rooms')
        .insert({
          property_id: property.id,
          room_number: nextNum,
          room_name: roomName.trim(),
          status: 'active'
        });
      if (error) throw error;
      await loadRooms();
      return true;
    } catch (err: any) {
      alert('Error adding room: ' + err.message);
      return false;
    }
  };

  // Get service status color based on days since last service
  const getServiceStatusColor = (daysSince: number | null, tier: string) => {
    if (daysSince === null) return { bg: 'var(--color-border)', color: 'var(--color-text-light)', label: 'Never serviced' };

    const thresholds: Record<string, { good: number; warning: number }> = {
      signature: { good: 90, warning: 120 },
      distinguished: { good: 150, warning: 180 },
      assured: { good: 300, warning: 365 }
    };
    const t = thresholds[tier] ?? thresholds['assured']!;

    if (daysSince <= t.good) return { bg: '#dcfce7', color: 'var(--color-success)', label: `${daysSince}d ago` };
    if (daysSince <= t.warning) return { bg: 'var(--color-warning-bg)', color: '#d97706', label: `${daysSince}d ago` };
    return { bg: 'var(--color-error-bg)', color: 'var(--color-error)', label: `${daysSince}d ago` };
  };

  // Room columns for DataTable
  const roomColumns: Column<Record<string, any>>[] = [
    {
      key: 'room_number',
      label: 'Room',
      sortable: true,
      render: (row: Record<string, any>) => (
        <div>
          <div className="fw-500">
            {row.room_name || row.room_number || `${row.bed_count} Bed Room`}
          </div>
          {row.bed_count && (
            <div className="fs-xs text-light">
              {row.bed_count} bed{row.bed_count !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'daysSinceService',
      label: 'Last Service',
      align: 'center',
      sortable: true,
      render: (row: Record<string, any>) => {
        const status = getServiceStatusColor(row.daysSinceService, property.hospitality_tier);
        return (
          <StatusBadge bg={status.bg} color={status.color}>
            {status.label}
          </StatusBadge>
        );
      }
    },
    {
      key: 'pending_service',
      label: 'Upcoming',
      align: 'center',
      render: (row: Record<string, any>) => {
        if (row.pendingService) {
          const date = formatDayMonth(row.pendingService.requested_date);
          return (
            <StatusBadge bg="var(--color-info-bg)" color="var(--color-info-text)">
              {date}
            </StatusBadge>
          );
        }
        return <span className="text-light fs-xs">-</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      align: 'center',
      render: (row: Record<string, any>) => {
        const isActive = (row.status || 'active') === 'active';
        return (
          <StatusBadge
            bg={isActive ? 'var(--color-success-bg)' : 'var(--color-border)'}
            color={isActive ? 'var(--color-success)' : 'var(--color-text-light)'}
          >
            {isActive ? 'Active' : 'Inactive'}
          </StatusBadge>
        );
      }
    }
  ];

  // Add room input state
  const [newRoomName, setNewRoomName] = useState('');
  const [addingRoom, setAddingRoom] = useState(false);
  const [showBulkBuilder, setShowBulkBuilder] = useState(false);
  const [showImportFromProposal, setShowImportFromProposal] = useState(false);

  const handleAddRoomClick = async () => {
    if (!newRoomName.trim()) return;
    setAddingRoom(true);
    const success = await handleAddRoom(newRoomName);
    if (success) setNewRoomName('');
    setAddingRoom(false);
  };

  // Generate weeks for matrix
  const getWeeks = () => {
    const weeks = [];
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - startDate.getDay() + (matrixWeekOffset * 7));

    for (let i = 0; i < numWeeks; i++) {
      const weekStart = new Date(startDate);
      weekStart.setDate(weekStart.getDate() + (i * 7));
      const weekNum = getWeekNumber(weekStart);
      const monthName = formatMonthShort(weekStart);
      weeks.push({
        weekNum,
        date: weekStart,
        label: `W${weekNum}`,
        month: monthName,
        isCurrentWeek: getWeekNumber(today) === weekNum && weekStart.getFullYear() === today.getFullYear()
      });
    }
    return weeks;
  };

  // Generate months for matrix
  const getMonths = () => {
    const months = [];
    const today = new Date();
    const startDate = new Date(today.getFullYear(), today.getMonth() + matrixMonthOffset, 1);

    for (let i = 0; i < numMonths; i++) {
      const monthStart = new Date(startDate);
      monthStart.setMonth(monthStart.getMonth() + i);
      const monthName = formatMonthShort(monthStart);
      const year = monthStart.getFullYear();
      months.push({
        date: monthStart,
        label: monthName,
        year: year,
        shortYear: year.toString().slice(-2),
        isCurrentMonth: today.getMonth() === monthStart.getMonth() && today.getFullYear() === year
      });
    }
    return months;
  };

  const calculateMonthStatus = (room: Record<string, any>, monthDate: Date) => {
    const midMonth = new Date(monthDate);
    midMonth.setDate(15);
    return calculateRoomStatus(room, midMonth);
  };

  // "Open matrix" button — pops the service calendar into a full-screen
  // overlay. Replaces the old List/Matrix inline toggle because a matrix
  // for 100+ rooms needs more horizontal room than the modal body has.
  const OpenMatrixButton = () => (
    <button
      type="button"
      className="btn btn-secondary"
      style={{ padding: '6px 12px', fontSize: 'var(--text-sm)', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
      onClick={() => setMatrixFullscreen(true)}
      title="Opens the service calendar matrix in a full-screen overlay — needed for large properties."
    >
      <span aria-hidden="true">⛶</span>
      Open matrix
    </button>
  );

  // Render matrix view
  const renderMatrixView = () => {
    const isWeekly = matrixGranularity === 'weekly';
    const columns: any[] = isWeekly ? getWeeks() : getMonths();
    const colCount = columns.length;

    const GranularityToggle = () => (
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '2px',
        background: 'var(--color-bg-tertiary)',
        borderRadius: 'var(--radius-md)'
      }}>
        <button
          onClick={() => setMatrixGranularity('weekly')}
          style={{
            padding: '4px 10px',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: 500,
            background: isWeekly ? 'var(--color-bg)' : 'transparent',
            color: isWeekly ? 'var(--color-text)' : 'var(--color-text-light)',
            boxShadow: isWeekly ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          Weekly
        </button>
        <button
          onClick={() => setMatrixGranularity('monthly')}
          style={{
            padding: '4px 10px',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            fontSize: 'var(--text-xs)',
            fontWeight: 500,
            background: !isWeekly ? 'var(--color-bg)' : 'transparent',
            color: !isWeekly ? 'var(--color-text)' : 'var(--color-text-light)',
            boxShadow: !isWeekly ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
          }}
        >
          Monthly
        </button>
      </div>
    );

    return (
      <div style={{
        background: 'var(--color-bg)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden'
      }}>
        {/* Matrix Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          flexWrap: 'wrap',
          gap: '0.75rem'
        }}>
          <div className="flex items-center gap-md">
            <GranularityToggle />
            <button
              className="btn btn-secondary"
              onClick={() => isWeekly ? setMatrixWeekOffset(p => p - numWeeks) : setMatrixMonthOffset(p => p - numMonths)}
              style={{ padding: '4px 8px', fontSize: 'var(--text-sm)' }}
            >
              &larr; Prev
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => isWeekly ? setMatrixWeekOffset(0) : setMatrixMonthOffset(0)}
              style={{ padding: '4px 8px', fontSize: 'var(--text-sm)' }}
            >
              Today
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => isWeekly ? setMatrixWeekOffset(p => p + numWeeks) : setMatrixMonthOffset(p => p + numMonths)}
              style={{ padding: '4px 8px', fontSize: 'var(--text-sm)' }}
            >
              Next &rarr;
            </button>
          </div>
          <div className="flex flex-wrap gap-md">
            {Object.entries(MATRIX_STATUS_CONFIG).map(([key, config]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--text-xs)' }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: 'var(--radius-sm)',
                  background: config.color
                }} />
                <span className="text-muted">{config.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Matrix Grid */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--text-sm)'
          }}>
            <thead>
              <tr>
                <th style={{
                  position: 'sticky',
                  left: 0,
                  background: 'var(--color-bg-secondary)',
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontWeight: 500,
                  borderBottom: '1px solid var(--color-border)',
                  minWidth: '100px',
                  zIndex: 1
                }}>
                  Room
                </th>
                {columns.map((col, idx) => {
                  const isCurrent = isWeekly ? col.isCurrentWeek : col.isCurrentMonth;
                  return (
                    <th key={idx} style={{
                      padding: '4px 2px',
                      textAlign: 'center',
                      fontWeight: isCurrent ? 700 : 400,
                      borderBottom: '1px solid var(--color-border)',
                      background: isCurrent ? 'rgba(189, 152, 47, 0.1)' : 'var(--color-bg-secondary)',
                      minWidth: isWeekly ? '28px' : '45px',
                      fontSize: 'var(--text-2xs)'
                    }}>
                      <div>{col.label}</div>
                      <div style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-2xs)' }}>
                        {isWeekly ? col.month : `'${col.shortYear}`}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {roomsLoading ? (
                <tr>
                  <td colSpan={colCount + 1} style={{ padding: '2rem', textAlign: 'center' }}>
                    <LoadingSpinner />
                  </td>
                </tr>
              ) : rooms.length === 0 ? (
                <tr>
                  <td colSpan={colCount + 1} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>
                    No rooms configured for this property.
                  </td>
                </tr>
              ) : (
                rooms.map((room) => (
                  <tr key={room.id}>
                    <td style={{
                      position: 'sticky',
                      left: 0,
                      background: 'var(--color-bg)',
                      padding: '6px 12px',
                      borderBottom: '1px solid var(--color-border)',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      zIndex: 1
                    }}>
                      {room.room_name || room.room_number || `${room.bed_count} Bed Room`}
                    </td>
                    {columns.map((col, idx) => {
                      const status = isWeekly
                        ? calculateRoomStatus(room, col.date)
                        : calculateMonthStatus(room, col.date);
                      const config = MATRIX_STATUS_CONFIG[status] ?? MATRIX_STATUS_CONFIG['pending']!;
                      const isCurrent = isWeekly ? col.isCurrentWeek : col.isCurrentMonth;
                      return (
                        <td
                          key={idx}
                          title={`${room.room_name || room.room_number} - ${col.label}${!isWeekly ? ` '${col.shortYear}` : ''}: ${config.label}`}
                          style={{
                            padding: '2px',
                            borderBottom: '1px solid var(--color-border)',
                            background: isCurrent ? 'rgba(189, 152, 47, 0.05)' : 'transparent'
                          }}
                        >
                          <div style={{
                            width: isWeekly ? '24px' : '40px',
                            height: '20px',
                            borderRadius: 'var(--radius-sm)',
                            background: config.color,
                            margin: '0 auto'
                          }} />
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Open-matrix button — property identity/status/nav all live on the parent modal header */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: '0.75rem',
      }}>
        <OpenMatrixButton />
      </div>

      {/* Rooms list — always inline */}
      <DataTable
        columns={roomColumns}
        data={rooms}
        loading={roomsLoading}
        searchable={true}
        searchPlaceholder="Search rooms..."
        searchKeys={['room_name', 'room_number']}
        defaultSort={{ key: 'room_number', direction: 'asc' }}
        headerActions={{ onSync: loadRooms }}
        actions={(row: Record<string, any>) => (
          <>
            <span
              className="action-icon"
              onClick={(e) => { e.stopPropagation(); setViewingRoom(row); }}
              title="View room details"
            >
              👁️
            </span>
            {canEdit && (
              <span
                className="action-icon"
                onClick={(e) => { e.stopPropagation(); setEditingRoom(row); }}
                title="Edit room"
              >
                ✏️
              </span>
            )}
            <span
              className="action-icon"
              onClick={(e) => {
                e.stopPropagation();
                setCertificatesContext({ type: 'room', id: row.id, name: row.room_name || row.room_number, propertyId: property.id, propertyName: property.property_name });
              }}
              title="View certificates"
            >
              📜
            </span>
            <span
              className="action-icon"
              onClick={(e) => {
                e.stopPropagation();
                setBookingsContext({ type: 'room', id: row.id, name: row.room_name || row.room_number, propertyId: property.id, propertyName: property.property_name });
              }}
              title="View bookings"
            >
              📅
            </span>
            <span
              className="action-icon"
              onClick={(e) => {
                e.stopPropagation();
                setServicesContext({ type: 'room', id: row.id, name: row.room_name || row.room_number, propertyId: property.id, propertyName: property.property_name });
              }}
              title="View service history"
            >
              🔧
            </span>
          </>
        )}
        pageSize={50}
        emptyMessage="No rooms configured for this property."
        resultsBarContent={canEdit ? (
          <div className="flex items-center gap-sm">
            <input
              id="add-room-input"
              type="text"
              className="form-input"
              value={newRoomName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewRoomName(e.target.value)}
              placeholder="New room name..."
              style={{ width: '200px', height: '32px', fontSize: 'var(--text-base)' }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddRoomClick();
                }
              }}
            />
            <button
              type="button"
              className="btn btn-primary"
              style={{ height: '32px', padding: '0 0.75rem', fontSize: 'var(--text-base)' }}
              onClick={handleAddRoomClick}
              disabled={!newRoomName.trim() || addingRoom}
            >
              {addingRoom ? '...' : 'Add'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ height: '32px', padding: '0 0.75rem', fontSize: 'var(--text-base)' }}
              onClick={() => setShowBulkBuilder(true)}
              title="Add multiple rooms at once"
            >
              Bulk Add
            </button>
            {property.proposal_id && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ height: '32px', padding: '0 0.75rem', fontSize: 'var(--text-base)' }}
                onClick={() => setShowImportFromProposal(true)}
                title="Re-create rooms from the linked proposal's room breakdown"
              >
                Import from proposal
              </button>
            )}
            <span style={{ marginLeft: '0.5rem', color: 'var(--color-text-light)', fontSize: 'var(--text-base)' }}>
              {rooms.length} room{rooms.length !== 1 ? 's' : ''}
              {' · '}
              <span style={{ fontStyle: 'italic' }}>One name at a time — use Bulk Add for several rooms.</span>
            </span>
          </div>
        ) : undefined}
        />

      {/* Matrix Fullscreen Overlay */}
      {matrixFullscreen && (
        <div
          className="modal-overlay"
          onClick={() => setMatrixFullscreen(false)}
          style={{ zIndex: 1200, padding: '1rem' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg)',
              borderRadius: 'var(--radius)',
              width: '100%',
              height: '100%',
              maxWidth: 'calc(100vw - 2rem)',
              maxHeight: 'calc(100vh - 2rem)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem 1.25rem',
              borderBottom: '1px solid var(--color-border)',
              flexShrink: 0,
            }}>
              <div>
                <h3 className="text-base fw-600 m-0">
                  Service calendar — {property.property_name}
                </h3>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', marginTop: '0.125rem' }}>
                  {rooms.length} room{rooms.length !== 1 ? 's' : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMatrixFullscreen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: 'var(--text-4xl)',
                  lineHeight: 1,
                  cursor: 'pointer',
                  color: 'var(--color-text-light)',
                  padding: '0.25rem 0.5rem',
                }}
                aria-label="Close matrix"
              >
                &times;
              </button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
              {renderMatrixView()}
            </div>
          </div>
        </div>
      )}

      {/* Room View Modal */}
      {viewingRoom && (
        <RoomViewModal
          room={viewingRoom as any}
          property={property as any}
          onClose={() => setViewingRoom(null)}
          onEdit={() => { setViewingRoom(null); setEditingRoom(viewingRoom); }}
          canEdit={canEdit}
          supabase={supabase}
        />
      )}

      {/* Room Edit Modal */}
      {editingRoom && (
        <RoomEditModal
          room={editingRoom as any}
          property={property as any}
          onClose={() => setEditingRoom(null)}
          onSave={async () => {
            setEditingRoom(null);
            await loadRooms();
          }}
          onDelete={async () => {
            setEditingRoom(null);
            await loadRooms();
          }}
          canEdit={canEdit}
          supabase={supabase}
        />
      )}

      {/* Bulk Room Builder Modal */}
      {showBulkBuilder && (
        <BulkRoomBuilder
          property={property}
          supabase={supabase}
          onClose={() => setShowBulkBuilder(false)}
          onSave={async () => {
            setShowBulkBuilder(false);
            await loadRooms();
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {/* Import rooms from a linked proposal */}
      {showImportFromProposal && (
        <ImportRoomsFromProposalModal
          property={property}
          supabase={supabase}
          existingRoomCount={rooms.length}
          onClose={() => setShowImportFromProposal(false)}
          onSave={async () => {
            setShowImportFromProposal(false);
            await loadRooms();
            if (onRefresh) onRefresh();
          }}
        />
      )}
    </div>
  );
}
